/**
 * Reusable multi-image uploader with previews.
 * - Limits: maxFiles (default 5), maxMb (default 10MB each)
 * - Accepts images only by default.
 * - Optional: emits onChange(files: File[]).
 */
import React, { useRef } from 'react';
import { Camera, X, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif';

export default function ImageUploader({
  files = [],
  onChange,
  maxFiles = 5,
  maxMb = 10,
  label = 'Attach photos (optional)',
  hint = 'Photos help us understand the scope. Up to 5 images, 10MB each.',
  accept = DEFAULT_ACCEPT,
  testId = 'image-uploader',
}) {
  const inputRef = useRef(null);

  const onSelect = (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = '';
    addFiles(incoming);
  };

  const addFiles = (incoming) => {
    const maxBytes = maxMb * 1024 * 1024;
    const filtered = [];
    for (const f of incoming) {
      if (!accept.split(',').some((t) => f.type === t.trim() || (t.endsWith('*') && f.type.startsWith(t.replace('*', ''))))) {
        toast.error(`${f.name}: unsupported file type`);
        continue;
      }
      if (f.size > maxBytes) {
        toast.error(`${f.name}: file too large (max ${maxMb}MB)`);
        continue;
      }
      filtered.push(f);
    }
    const combined = [...files, ...filtered].slice(0, maxFiles);
    if (files.length + filtered.length > maxFiles) {
      toast.warning(`Only the first ${maxFiles} files will be attached.`);
    }
    if (onChange) onChange(combined);
  };

  const removeAt = (idx) => {
    const next = [...files];
    next.splice(idx, 1);
    if (onChange) onChange(next);
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="text-xs text-slate-500">{files.length} / {maxFiles}</span>
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); addFiles(Array.from(e.dataTransfer.files || [])); }}
        className="relative cursor-pointer rounded-xl border-2 border-dashed border-slate-300 hover:border-[hsl(var(--primary))] bg-slate-50 hover:bg-white px-4 py-6 sm:py-8 text-center transition-colors"
        data-testid={`${testId}-dropzone`}
      >
        <Upload className="h-7 w-7 mx-auto text-slate-400" />
        <p className="mt-2 text-sm text-slate-700">
          <span className="font-semibold text-[hsl(var(--primary))]">Click to upload</span> or drag &amp; drop
        </p>
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          onChange={onSelect}
          className="hidden"
          data-testid={`${testId}-input`}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid={`${testId}-previews`}>
          {files.map((f, i) => (
            <div key={i} className="relative rounded-lg overflow-hidden bg-slate-100 aspect-square border border-slate-200" data-testid={`${testId}-preview-${i}`}>
              {f.type?.startsWith('image/') ? (
                <img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" onLoad={(e) => URL.revokeObjectURL(e.currentTarget.src)} />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-slate-400"><ImageIcon className="h-6 w-6" /></div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/65 text-white flex items-center justify-center hover:bg-black/80"
                aria-label="Remove"
                data-testid={`${testId}-remove-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-1.5 py-0.5 truncate">{f.name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
